// Employee Score → Manage → a custom metric's own tables, as Excel sheets.
//
// The Download button writes the table on screen, so these builders take the
// rows SubMetricsManagePanel is rendering — already role-scoped and sorted —
// and only lay them out as cells. They never re-sort or re-filter.
//
// One difference from the screen, so the number column can be sorted and
// summed: the note a cell prints after its number ("from Jun 2026" for a value
// carried forward, "ignored 12" for an out-of-range answer) goes in a column of
// its own after the number. A note column exists only when some row has that
// note, so most files have neither.

import type { TableCell, TableSheet } from '../utils/tableXlsx';

/** One row of a Value grid or of the Result table, holding what the screen prints. */
export interface SubMetricSheetRow {
  employee_name: string;
  employee_code: string;
  store_name: string;
  store_code: string;
  store_address: string;
  /** The exit date as printed, for somebody who has left. The column exists only when some row has one. */
  exitDate?: string | null;
  /** The number in the Value / Result cell; null when the cell is blank or '—'. */
  value: number | null;
  /** The month label the cell prints after "from " when its value was carried forward. */
  carriedFrom?: string | null;
  /** The out-of-range answer the cell prints after "ignored ". */
  ignoredValue?: number | null;
}

/** What the screen prints in place of a missing value. In the file that is an empty cell. */
const PLACEHOLDER = '—';

const EMPLOYEE_HEADERS = ['Employee Name', 'Employee Code', 'Store Name', 'Store Code', 'Store Address'];
const STORE_HEADERS = ['Store Code', 'Store Name', 'Store Address'];

// A store with no code reaches the panel's rows already holding the placeholder,
// so it is checked for here as well as the empty string. Codes are passed
// through untouched — "000123" must stay text.
const text = (value: string): TableCell => (!value || value === PLACEHOLDER ? null : value);

const employeeCells = (r: SubMetricSheetRow): TableCell[] => [
  text(r.employee_name), text(r.employee_code), text(r.store_name), text(r.store_code), text(r.store_address),
];
const storeCells = (r: SubMetricSheetRow): TableCell[] => [
  text(r.store_code), text(r.store_name), text(r.store_address),
];

// The Exit Date column of an employee table: only when somebody on the list has left.
const exitHeader = (rows: SubMetricSheetRow[]): string[] => (rows.some((r) => !!r.exitDate) ? ['Exit Date'] : []);
const exitCell = (rows: SubMetricSheetRow[], r: SubMetricSheetRow): TableCell[] =>
  (rows.some((row) => !!row.exitDate) ? [r.exitDate || null] : []);

const sheetWithNotes = (
  name: string,
  headers: string[],
  rows: SubMetricSheetRow[],
  cells: (row: SubMetricSheetRow) => TableCell[],
): TableSheet => {
  const anyCarried = rows.some((r) => !!r.carriedFrom);
  const anyIgnored = rows.some((r) => r.ignoredValue != null);
  return {
    name,
    headers: [
      ...headers,
      ...(anyCarried ? ['Carried from'] : []),
      ...(anyIgnored ? ['Ignored value'] : []),
    ],
    rows: rows.map((r) => [
      ...cells(r),
      ...(anyCarried ? [r.carriedFrom || null] : []),
      ...(anyIgnored ? [r.ignoredValue ?? null] : []),
    ]),
  };
};

/**
 * A sub-metric's Value grid, in the layout on screen: store-led for a store-scope
 * manual sub-metric, employee-led otherwise. Value is the number the cell holds —
 * for a manual sub-metric whatever its input holds, saved or not.
 */
export const valueGridSheet = (
  name: string,
  layout: 'store' | 'employee',
  rows: SubMetricSheetRow[],
): TableSheet =>
  sheetWithNotes(
    name,
    [...(layout === 'store' ? STORE_HEADERS : [...EMPLOYEE_HEADERS, ...exitHeader(rows)]), 'Value'],
    rows,
    (r) => [...(layout === 'store' ? storeCells(r) : [...employeeCells(r), ...exitCell(rows, r)]), r.value],
  );

// The Result cell prints a whole number as it is and anything else to two decimals.
const shownResult = (value: number | null): number | null =>
  value == null || Number.isInteger(value) ? value : Number(value.toFixed(2));

/** The Result table: the formula's value per employee, as the cell prints it. */
export const resultSheet = (rows: SubMetricSheetRow[]): TableSheet =>
  sheetWithNotes(
    'Result',
    [...EMPLOYEE_HEADERS, ...exitHeader(rows), 'Result'],
    rows,
    (r) => [...employeeCells(r), ...exitCell(rows, r), shownResult(r.value)],
  );
